"use client";

import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";

const DEFAULT_RELEASE_NAME = "imags-react";

let sentryInitialized = false;

function readEnvValue(value: string | undefined) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function readEnvFlag(value: string | undefined) {
  const normalized = readEnvValue(value).toLowerCase();
  return (
    normalized === "1" ||
    normalized === "true" ||
    normalized === "yes" ||
    normalized === "on"
  );
}

export function getSentryDsn() {
  return readEnvValue(process.env.NEXT_PUBLIC_SENTRY_DSN);
}

export function isSentryEnabled() {
  return getSentryDsn().length > 0;
}

export function isSentryTestCrashEnabled() {
  return readEnvFlag(process.env.NEXT_PUBLIC_ENABLE_SENTRY_TEST_CRASH);
}

export function getSentryEnvironment() {
  return (
    readEnvValue(process.env.NEXT_PUBLIC_APP_ENV) ||
    readEnvValue(process.env.NODE_ENV) ||
    "development"
  );
}

export function getSentryReleaseName() {
  return (
    readEnvValue(process.env.NEXT_PUBLIC_SENTRY_RELEASE_NAME) ||
    DEFAULT_RELEASE_NAME
  );
}

export function getSentryReleaseVersion() {
  return readEnvValue(process.env.NEXT_PUBLIC_APP_VERSION) || "0.1.0";
}

export function getSentryRelease() {
  return `${getSentryReleaseName()}@${getSentryReleaseVersion()}`;
}

export function getSentryDist() {
  return (
    readEnvValue(process.env.NEXT_PUBLIC_APP_BUILD_ID) ||
    getSentryReleaseVersion()
  );
}

export function canTriggerSentryTestCrash() {
  return (
    isSentryEnabled() &&
    isSentryTestCrashEnabled() &&
    Capacitor.isNativePlatform()
  );
}

function getBaseTags() {
  return {
    app_env: getSentryEnvironment(),
    app_build_id: getSentryDist(),
    capacitor_platform: Capacitor.getPlatform(),
    native_platform: String(Capacitor.isNativePlatform()),
  };
}

async function setNativeAppContext() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const info = await App.getInfo();

    Sentry.setTags({
      native_app_id: info.id,
      native_app_version: info.version,
      native_app_build: info.build,
    });
    Sentry.setContext("native_app", {
      id: info.id,
      name: info.name,
      version: info.version,
      build: info.build,
    });
  } catch (error) {
    console.warn("[sentry] Failed to read native app info", error);
  }
}

export function initSentry() {
  if (typeof window === "undefined" || sentryInitialized || !isSentryEnabled()) {
    return false;
  }

  sentryInitialized = true;

  Sentry.init(
    {
      dsn: getSentryDsn(),
      environment: getSentryEnvironment(),
      release: getSentryRelease(),
      dist: getSentryDist(),
      enableAutoSessionTracking: false,
      attachStacktrace: true,
      enableNative: Capacitor.isNativePlatform(),
      enableNativeCrashHandling: Capacitor.isNativePlatform(),
      enableNativeNagger: false,
      maxBreadcrumbs: 100,
      normalizeDepth: 6,
      initialScope: {
        tags: getBaseTags(),
      },
      beforeSend(event) {
        event.tags = {
          ...getBaseTags(),
          ...(event.tags ?? {}),
        };

        return event;
      },
    },
    SentryReact.init
  );

  void setNativeAppContext();
  return true;
}

export async function triggerSentryTestCrash(source = "unknown") {
  if (!canTriggerSentryTestCrash()) {
    return false;
  }

  const eventMessage = `IMAGS native crash test requested (${source})`;

  Sentry.addBreadcrumb({
    category: "qa.test",
    message: "Manual Sentry crash test requested",
    level: "fatal",
    data: {
      source,
      environment: getSentryEnvironment(),
      release: getSentryRelease(),
      dist: getSentryDist(),
      platform: Capacitor.getPlatform(),
    },
  });

  Sentry.withScope((scope) => {
    scope.setLevel("fatal");
    scope.setTag("test_crash_source", source);
    scope.setTag("test_crash_native", "true");
    scope.setContext("test_crash", {
      source,
      environment: getSentryEnvironment(),
      release: getSentryRelease(),
      dist: getSentryDist(),
      platform: Capacitor.getPlatform(),
    });
    Sentry.captureMessage(eventMessage, "fatal");
  });

  try {
    await Sentry.flush(2000);
  } catch (error) {
    console.warn("[sentry] Failed to flush pre-crash test event", error);
  }

  const triggerNativeCrash = () => {
    Sentry.nativeCrash();
  };

  if (typeof window === "undefined") {
    triggerNativeCrash();
  } else {
    window.setTimeout(triggerNativeCrash, 120);
  }

  return true;
}

type SentryScopeSnapshot = {
  authStatus: string | null;
  userId: string | null;
  userCode: string | null;
  provider: string | null;
  isAdmin: boolean;
  sessionId: string | null;
  sessionStatus: string | null;
  sessionActive: boolean;
};

export function syncSentryScope(snapshot: SentryScopeSnapshot) {
  if (!isSentryEnabled() || !sentryInitialized) {
    return;
  }

  Sentry.setUser(
    snapshot.userId
      ? {
          id: snapshot.userId,
          username: snapshot.userCode || undefined,
        }
      : null
  );

  Sentry.setTags({
    auth_status: snapshot.authStatus || "unknown",
    auth_provider: snapshot.provider || "none",
    auth_is_admin: String(snapshot.isAdmin),
    session_id: snapshot.sessionId || "none",
    session_status: snapshot.sessionStatus || "none",
    session_active: String(snapshot.sessionActive),
  });

  Sentry.setContext("auth", {
    status: snapshot.authStatus,
    provider: snapshot.provider,
    isAdmin: snapshot.isAdmin,
    userCode: snapshot.userCode,
  });

  Sentry.setContext(
    "session",
    snapshot.sessionId || snapshot.sessionStatus
      ? {
          id: snapshot.sessionId,
          status: snapshot.sessionStatus,
          active: snapshot.sessionActive,
        }
      : null
  );
}
